import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Hairline, Icon, Section } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { PrepareListing } from "@/components/PrepareListing";
import { mergedItem, scanStore } from "@/lib/scanStore";
import { supabase } from "@/integrations/supabase/client";

const PLATFORM_FEES: Record<string, number> = {
  Depop: 0.10,
  Poshmark: 0.20,
  eBay: 0.13,
  Etsy: 0.08,
  "Etsy Vintage": 0.08,
  "1stDibs": 0.20,
  Grailed: 0.09,
  Vestiaire: 0.22,
};

// The default platform breakdown shows every major resale destination we
// support, regardless of which one(s) FIG sourced its comps from. This lets
// collectors compare fee-adjusted net across the full board, not just the
// platform that happened to have the most listings.
const DEFAULT_PLATFORMS: string[] = [
  "eBay", "Depop", "Poshmark", "Grailed", "Etsy", "Vestiaire",
];

function parseRange(s?: string | null): [number, number] | null {
  if (!s) return null;
  const nums = s.replace(/[^0-9–\-—.\s]/g, "").split(/[–\-—]/).map((x) => parseFloat(x.trim()));
  if (nums.length < 2 || !isFinite(nums[0]) || !isFinite(nums[1])) return null;
  return [Math.round(nums[0]), Math.round(nums[1])];
}

function getBestPlatform(
  sources: string[] | undefined,
  itemCategory: string | undefined | null,
  avgScore: number | undefined,
  specialChars: string[] | undefined,
) {
  const hasPremiumChars = (specialChars || []).some((c) =>
    ["hand-beaded", "silk", "cashmere", "hand-embroidered"].includes(String(c).toLowerCase()),
  );
  const isHighValue = (avgScore ?? 0) >= 85;
  const category = (itemCategory || "").toLowerCase();

  if (hasPremiumChars && isHighValue) {
    return {
      name: "Etsy Vintage",
      reason: "Premium authenticated vintage with special characteristics performs best with collectors on Etsy Vintage",
      fee: 0.08,
    };
  }
  if (isHighValue && !hasPremiumChars) {
    return {
      name: "Depop",
      reason: "Authenticated designer vintage has strong demand from Depop's fashion-forward buyer base",
      fee: 0.10,
    };
  }
  if (category.includes("bag") || category.includes("accessory") || category.includes("shoes")) {
    return {
      name: "eBay",
      reason: "Vintage accessories reach the widest audience on eBay with strong search visibility",
      fee: 0.13,
    };
  }
  return {
    name: "Depop",
    reason: "Broad vintage clothing demand makes Depop the strongest default platform",
    fee: 0.10,
  };
}

export default function Price() {
  const nav = useNavigate();
  const item = useMemo(() => mergedItem(scanStore.getResult()), []);
  const p = item.pricing;

  // FIG Verify pricing guidance (replaces the legacy suggested-price band).
  const verify = useMemo(() => scanStore.getVerifyResult(), []);
  const guidance = verify?.pricing?.guidance ?? null;
  const sources = (verify?.pricing?.sources as string[] | undefined) ?? [];
  const specialChars =
    (verify?.observations as { special_characteristics?: string[] } | undefined)
      ?.special_characteristics ?? [];
  const avgScore = (verify?.meta as { avg_score?: number } | undefined)?.avg_score;
  const itemCategory = (verify?.meta as { item_category?: string } | undefined)?.item_category;
  const pricingRationale =
    (guidance as { pricing_rationale?: string } | null)?.pricing_rationale ?? "";

  // Richer breakdown for the Pricing notes section — replaces the
  // single-line "Based on N comparable listings" with a multi-row
  // explanation. Pull from the metadata the edge function already
  // surfaces: comparable_count, peer_match_count, source mix
  // (data_coverage.engines_succeeded), and channel_calibration era.
  const pricingMeta = verify?.pricing as
    | {
        comparable_count?: number;
        peer_match_count?: number;
        data_coverage?: { engines_succeeded?: string[] };
        channel_calibration?: {
          era_class?: string;
          multiplier?: number;
          source_mult?: number;
          era_bump?: number;
        };
      }
    | undefined;
  const compTotal = pricingMeta?.comparable_count ?? 0;
  const peerMatches = pricingMeta?.peer_match_count ?? 0;
  const engineList = pricingMeta?.data_coverage?.engines_succeeded ?? [];
  // eraClass and channelMult derivations removed — the Haiku educational
  // rationale now narrates the era + market reasoning directly, no need
  // for a separate "calibrated for pre-2000 vintage" templated line.

  const engineLabel = engineList.length === 0
    ? null
    : engineList
        .map((e) => e === "ebay" ? "eBay sold" : e === "grailed" ? "Grailed sold" : e)
        .join(", ");

  // Derive numeric recommended low/high from the guidance string (edge function
  // returns it formatted as "$X–$Y").
  const recRange = parseRange(guidance?.recommended_range);
  const midPrice = recRange ? Math.round((recRange[0] + recRange[1]) / 2) : null;

  const bestPlatform = useMemo(
    () => getBestPlatform(sources, itemCategory, avgScore, specialChars),
    [sources, itemCategory, avgScore, specialChars],
  );

  // Platform breakdown — always shows every marketplace in DEFAULT_PLATFORMS,
  // so collectors can compare fee-adjusted net across all of them rather than
  // only the platforms FIG happened to source comps from. `sources` and the
  // bestPlatform pick still influence ORDERING (best-pick first, anything
  // FIG sourced next, then the rest), but the row set itself is fixed.
  const platformRows = useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    const push = (name: string) => {
      if (seen.has(name)) return;
      seen.add(name);
      ordered.push(name);
    };
    push(bestPlatform.name);
    for (const s of sources) push(s);
    for (const p of DEFAULT_PLATFORMS) push(p);
    return ordered
      // Drop platforms we don't have a fee schedule for — defensive against a
      // future source label we haven't mapped yet.
      .filter((name) => PLATFORM_FEES[name] != null)
      .map((name) => {
        const fee = PLATFORM_FEES[name];
        const price = midPrice ?? p.suggested;
        return {
          name,
          price,
          fee: Math.round(fee * 100),
          net: Math.round(price * (1 - fee)),
          isRecommended: name === bestPlatform.name,
        };
      });
  }, [sources, bestPlatform, midPrice, p.suggested]);

  const savedScanId = typeof window !== "undefined" ? sessionStorage.getItem("fig_scan_saved_id") : null;

  // Pull the current verification block. Prefer the in-memory scan store
  // (reflects in-session collector overrides), then fall back to the DB row.
  const [verification, setVerification] = useState<{
    tier?: "photo_verified" | "self_reported" | "uncertain";
  } | null>(() => {
    const cached = scanStore.getResult();
    return (cached?.verification as { tier?: "photo_verified" | "self_reported" | "uncertain" } | null) ?? null;
  });

  useEffect(() => {
    if (verification || !savedScanId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("scans")
        .select("verification")
        .eq("id", savedScanId)
        .maybeSingle();
      if (cancelled || !data) return;
      setVerification((data.verification as { tier?: "photo_verified" | "self_reported" | "uncertain" } | null) ?? null);
    })();
    return () => { cancelled = true; };
  }, [savedScanId, verification]);

  // Always start at the top when entering the market report.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 40 }}>
        {/* ─── Top bar ─────────────────────────────────────────── */}
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}>
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">The Ledger</span>
          <div style={{ width: 30 }} />
        </div>

        {/* ─── 1. MARKET HEADER ────────────────────────────────── */}
        {(() => {
          const vItem = (verify?.item || {}) as {
            detected_type?: string;
            brand_detected?: string | null;
            estimated_era?: string;
            material_description?: string;
            color_description?: string;
          };
          const headerCategory = itemCategory || item.category || "Garment";
          const brand = vItem.brand_detected || item.brand;
          const detected = vItem.detected_type || item.title;
          const headerTitle = brand ? `${detected} by ${brand}` : detected;
          const era = vItem.estimated_era || item.era;
          return (
            <Section pad={14} eyebrow={`Market Report · ${headerCategory}`} title={headerTitle}>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 14, color: "hsl(var(--ink-2))", marginTop: -4,
              }}>
                {brand} · {era}
              </div>
            </Section>
          );
        })()}

        {/* ─── 2. FIG VERIFY PRICING GUIDANCE ─────────────────── */}
        <Section pad={18} eyebrow="Fig suggests">
          {guidance ? (
            <div>
              {/* "Market: ${low}–${high}" used to lead here, but the raw
                  min/max of comp prices was confusingly wide whenever the
                  comp set caught an outlier — see Result.tsx for the same
                  removal rationale. Recommended range alone is the lead. */}
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800,
                fontSize: 36, color: "hsl(var(--ink))", lineHeight: 1.1,
              }}>
                {guidance.recommended_range}
              </div>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 13, color: "hsl(var(--ink-2))", marginTop: 8,
                lineHeight: 1.4,
              }}>
                {guidance.condition_note}
              </div>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                color: "hsl(var(--ink-2))", marginTop: 10,
              }}>
                Based on {guidance.based_on}
              </div>
            </div>
          ) : (
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 14, color: "hsl(var(--ink-2))", lineHeight: 1.5,
            }}>
              Pricing data unavailable for this item — try searching Depop and Etsy Vintage manually.
            </div>
          )}
          {(compTotal > 0 || pricingRationale) && (
            <div style={{ marginTop: 14 }}>
              <div className="caps" style={{ marginBottom: 8 }}>Why this price</div>

              {/* Educational rationale — Haiku-generated 2-4 sentences
                  teaching the seller WHY this piece prices where it does
                  in terms of brand tier, era, material, and market reality.
                  Falls back to a server template if Haiku didn't run. */}
              {pricingRationale && (
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 14, color: "hsl(var(--ink))", lineHeight: 1.6,
                  marginBottom: 12,
                }}>
                  {pricingRationale}
                </div>
              )}

              {/* Data-source metadata — small, secondary, so the educational
                  rationale leads. Comp count + which platforms contributed. */}
              {compTotal > 0 && (
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10, color: "hsl(var(--ink-2))", lineHeight: 1.5,
                  letterSpacing: "0.05em",
                  marginBottom: 6,
                }}>
                  Based on {compTotal} comparable sold listing{compTotal === 1 ? "" : "s"}
                  {peerMatches > 0 ? `, ${peerMatches} closely matching brand/material/era` : ""}
                  {engineLabel ? ` · ${engineLabel}` : ""}
                </div>
              )}

              {/* Honest caveat */}
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10, color: "hsl(var(--ink-2))", lineHeight: 1.5,
                letterSpacing: "0.05em",
                marginTop: 10, paddingTop: 8,
                borderTop: "1px dashed hsl(var(--sand-deep))",
              }}>
                Market signal, not a guarantee — verify against current asking prices on your target platform before listing.
              </div>
            </div>
          )}
          {specialChars.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div className="caps" style={{ marginBottom: 6 }}>Premium characteristics</div>
              <ul style={{
                listStyle: "none", padding: 0, margin: 0,
                fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13,
                color: "hsl(var(--ink))", lineHeight: 1.6,
              }}>
                {specialChars.map((c, i) => (
                  <li key={i}>✦ {c}</li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        {/* ─── 5. WHERE TO SELL ────────────────────────────────── */}
        <Section
          pad={22}
          eyebrow="Where to sell"
          title="Platform breakdown"
          meta={<span className="caps">after fees</span>}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {platformRows.map((pl) => (
              <Card
                key={pl.name}
                accent={pl.isRecommended ? "hsl(var(--rust))" : undefined}
                style={{ padding: "12px 14px", position: "relative" }}
              >
                {pl.isRecommended && (
                  <div style={{
                    position: "absolute", top: -8, right: 12,
                    background: "hsl(var(--rust))", color: "hsl(var(--rust-fg))",
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                    letterSpacing: "0.14em", padding: "2px 7px", textTransform: "uppercase",
                  }}>
                    recommended
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
                  <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 17, color: "hsl(var(--ink))" }}>
                    {pl.name}
                  </span>
                  <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 20, color: "hsl(var(--ink))" }}>
                    ${pl.price}
                  </span>
                </div>
                <div style={{
                  display: "flex", justifyContent: "space-between",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "hsl(var(--ink-2))",
                }}>
                  <span>{pl.isRecommended ? bestPlatform.reason : `${pl.name} listing`}</span>
                  <span>−{pl.fee}% · net ${pl.net}</span>
                </div>
              </Card>
            ))}
          </div>
        </Section>

        {/* ─── 6. SOURCING NOTE ────────────────────────────────── */}
        <Section pad={18}>
          <Hairline dashed />
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
            color: "hsl(var(--ink-2))", marginTop: 8, lineHeight: 1.5,
          }}>
            Based on 147 sold listings across Depop, Grailed, eBay, and Etsy (last 30d). Net accounts for platform fees and shipping at median rate.
          </div>
        </Section>

        {/* ─── 7. PRIMARY CTAs ─────────────────────────────────── */}
        {/* Prepare-listing workflow lives here so collectors can finish
            the listing without leaving the market report. */}
        <Section pad={22}>
          <PrepareListing item={item} verification={verification} savedScanId={savedScanId} />
        </Section>

        <Section pad={18}>
          <button
            className="btn"
            style={{
              width: "100%", padding: "14px",
              background: "hsl(var(--cream))",
              border: "1px solid hsl(var(--ink))",
              color: "hsl(var(--ink))", fontSize: 14,
              cursor: "pointer",
            }}
            onClick={() => nav("/result")}
          >
            ← Return to verification page
          </button>
        </Section>
      </div>
    </MobileFrame>
  );
}
